import { useState, useEffect, useRef, useCallback } from 'react'
import { useDesktopStore, WindowId } from '@/stores/desktopStore'
import { gsap } from 'gsap'
import './DesktopLanding.css'
import { BOOKS, DEFAULT_STATE, LANDING_FOOTER, type BookData } from '@/data/landing'



export function DesktopLanding() {
  const { openWindow, setDesktopReady } = useDesktopStore()
  const [activeBookIndex, setActiveBookIndex] = useState<number | null>(null)
  const bookRefs = useRef<(HTMLDivElement | null)[]>([])
  const bookTimelines = useRef<gsap.core.Timeline[]>([])
  const overlayTextRef = useRef<HTMLDivElement>(null)
  const dotGridRef = useRef<HTMLDivElement>(null)
  const landingRef = useRef<HTMLDivElement>(null)

  const createDotGrid = useCallback(() => {
    if (!dotGridRef.current) return
    const dotGrid = dotGridRef.current
    dotGrid.innerHTML = ''
    const fullWidth = window.innerWidth
    const fullHeight = window.innerHeight
    const spacing = 45
    const cols = Math.ceil(fullWidth / spacing) + 2
    const rows = Math.ceil(fullHeight / spacing) + 2
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const dot = document.createElement('div')
        dot.className = 'dot-grid__dot'
        dot.textContent = '✦'
        dot.style.left = `${x * spacing}px`
        dot.style.top = `${y * spacing}px`
        dotGrid.appendChild(dot)
      }
    }
  }, [])

  useEffect(() => {
    createDotGrid()
    window.addEventListener('resize', createDotGrid)
    return () => window.removeEventListener('resize', createDotGrid)
  }, [createDotGrid])

  useEffect(() => {
    const landing = landingRef.current
    const dotGrid = dotGridRef.current
    if (!landing || !dotGrid) return
    let isMouseMoving = false
    let mouseTimeout: ReturnType<typeof setTimeout>
    const updateDots = (mouseX: number, mouseY: number) => {
      const dots = dotGrid.querySelectorAll('.dot-grid__dot') as NodeListOf<HTMLElement>
      const velocity = isMouseMoving ? 1.5 : 1
      const maxDistance = isMouseMoving ? 150 : 100
      dots.forEach((dot) => {
        const dotX = parseInt(dot.style.left)
        const dotY = parseInt(dot.style.top)
        const distance = Math.sqrt(Math.pow(mouseX - dotX, 2) + Math.pow(mouseY - dotY, 2))
        if (distance < maxDistance) {
          const intensity = Math.pow(1 - distance / maxDistance, 1.5) * velocity
          dot.style.color = `rgba(255, 255, 255, ${Math.min(intensity, 1)})`
          const angle = Math.atan2(dotY - mouseY, dotX - mouseX)
          const pushDistance = intensity * 12
          const newX = Math.cos(angle) * pushDistance
          const newY = Math.sin(angle) * pushDistance
          dot.style.transform = `translate(${newX}px, ${newY}px) scale(${1 + intensity * 1.2})`
        } else {
          dot.style.color = '#333'
          dot.style.transform = 'none'
        }
      })
    }
    const handleMouseMove = (e: MouseEvent) => {
      const rect = dotGrid.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top
      isMouseMoving = true
      clearTimeout(mouseTimeout)
      updateDots(mouseX, mouseY)
      mouseTimeout = setTimeout(() => { isMouseMoving = false }, 100)
    }
    const handleMouseLeave = () => {
      const dots = dotGrid.querySelectorAll('.dot-grid__dot') as NodeListOf<HTMLElement>
      dots.forEach((dot) => { dot.style.color = '#333'; dot.style.transform = 'none' })
    }
    landing.addEventListener('mousemove', handleMouseMove)
    landing.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      landing.removeEventListener('mousemove', handleMouseMove)
      landing.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [])

  useEffect(() => {
    bookRefs.current.forEach((book, index) => {
      if (!book) return
      const bookImage = book.querySelector('.books__image') as HTMLElement
      const bookEffect = book.querySelector('.books__effect') as HTMLElement
      const bookLight = book.querySelector('.books__light') as HTMLElement
      const pages = book.querySelectorAll('.books__page')
      const bookShadow = document.querySelectorAll('.book-shadow__item')[index]
      gsap.set(bookImage, { boxShadow: 'var(--book-shadow) 10px 5px 20px, var(--book-shadow) 20px 0px 30px' })
      gsap.set(bookLight, { opacity: 0.1 })
      gsap.set(pages, { x: 0 })
      const hoverIn = gsap.timeline({ paused: true, defaults: { duration: 0.7, ease: 'power2.out' } })
      hoverIn.to(bookImage, { translateX: -10, scaleX: 0.96, boxShadow: 'var(--book-shadow-strong) 20px 5px 20px, var(--book-shadow) 30px 0px 30px' }, 0)
      if (bookShadow) hoverIn.to(bookShadow, { width: 130, opacity: 0.8 }, 0)
      hoverIn.to(bookEffect, { marginLeft: 10 }, 0)
      hoverIn.to(bookLight, { opacity: 0.2 }, 0)
      if (pages.length >= 3) {
        hoverIn.to(pages[0], { x: '2px', ease: 'power1.inOut' }, 0)
        hoverIn.to(pages[1], { x: '0px', ease: 'power1.inOut' }, 0)
        hoverIn.to(pages[2], { x: '-2px', ease: 'power1.inOut' }, 0)
      }
      bookTimelines.current[index] = hoverIn
    })
    setTimeout(() => setDesktopReady(true), 500)
  }, [setDesktopReady])

  const handleBookHover = (index: number) => {
    if (activeBookIndex !== null && activeBookIndex !== index && bookTimelines.current[activeBookIndex]) {
      bookTimelines.current[activeBookIndex].reverse()
    }
    setActiveBookIndex(index)
    if (bookTimelines.current[index]) bookTimelines.current[index].play()
    if (overlayTextRef.current) overlayTextRef.current.textContent = BOOKS[index].title
  }

  const handleShelfLeave = () => {
    if (activeBookIndex !== null && bookTimelines.current[activeBookIndex]) {
      bookTimelines.current[activeBookIndex].reverse()
    }
    setActiveBookIndex(null)
    if (overlayTextRef.current) overlayTextRef.current.textContent = DEFAULT_STATE.title
  }

  const handleBookClick = (id: WindowId) => openWindow(id)

  const currentTitle = activeBookIndex !== null ? BOOKS[activeBookIndex].title : DEFAULT_STATE.title

  return (
    <div className="desktop-landing dark-mode" ref={landingRef}>
      <div ref={dotGridRef} className="dot-grid" />
      <div className="book-title-overlay">
        <div className="book-title-text" ref={overlayTextRef}>{currentTitle}</div>
      </div>
      <div className="landing-footer">
        <div className="landing-footer__left"><span>Mindfulness</span><span>Presence</span></div>
        <div className="landing-footer__center">Est. 2025 • Creative Development</div>
        <div className="landing-footer__right"><span>34.0522° N</span><span>118.2437° W</span></div>
      </div>
      <div className="shelf-container" onMouseLeave={handleShelfLeave}>
        <div className="book-shadow">{BOOKS.map((_, i) => <div key={i} className="book-shadow__item" />)}</div>
        <div className="shelf" />
        <div className="books-wrapper">
          {BOOKS.map((book, index) => (
            <div key={book.id} ref={(el) => { bookRefs.current[index] = el }} className="books__item" onMouseEnter={() => handleBookHover(index)} onClick={() => handleBookClick(book.id)}>
              <div className="books__container">
                <div className="books__cover">
                  <div className="books__back-cover" />
                  <div className="books__inside"><div className="books__page" /><div className="books__page" /><div className="books__page" /></div>
                  <div className="books__image"><img src={book.image} alt={book.title} /><div className="books__effect" /><div className="books__light" /></div>
                  <div className="books__hitbox" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="book-descriptions">
          <div className={`book-description ${activeBookIndex === null ? 'active' : ''}`}><h3>{DEFAULT_STATE.title}</h3><div className="author"></div><div className="lines-animation"><p>{DEFAULT_STATE.description}</p></div></div>
          {BOOKS.map((book, index) => (
            <div key={book.id} className={`book-description ${activeBookIndex === index ? 'active' : ''}`}><h3>{book.title}</h3><div className="author">{book.author}</div><div className="lines-animation"><p>{book.description}</p></div></div>
          ))}
        </div>
      </div>
    </div>
  )
}
